import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { navItems, site, marqueeLines } from "@/mocks/site";
import { posts } from "@/mocks/posts";

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setKeyword("");
  }, [location.pathname]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const results = useMemo(() => {
    const key = keyword.trim();
    if (!key) return [];
    return posts
      .filter(
        (post) =>
          post.title.includes(key) ||
          post.excerpt.includes(key) ||
          post.tags.some((tag) => tag.includes(key)),
      )
      .slice(0, 6);
  }, [keyword]);

  const goToResult = (id: string) => {
    setSearchOpen(false);
    setKeyword("");
    navigate(`/post/${id}`);
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="hidden md:block h-8 bg-foreground-950 text-background-50 overflow-hidden">
        <div className="flex h-full items-center">
          <span className="shrink-0 px-4 h-full flex items-center bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
            <span className="w-3.5 h-3.5 mr-1 flex items-center justify-center">
              <i className="ri-volume-up-line"></i>
            </span>
            吃瓜热线
          </span>
          <div className="flex-1 overflow-hidden">
            <div className="flex whitespace-nowrap animate-marquee">
              {[...marqueeLines, ...marqueeLines].map((line, index) => (
                <span key={`${line}-${index}`} className="mx-6 text-xs text-background-200">
                  {line}
                </span>
              ))}
            </div>
          </div>
          <span className="shrink-0 px-4 text-xs text-background-300 whitespace-nowrap hidden lg:block">
            {site.updateTime}
          </span>
        </div>
      </div>

      <div
        className={`w-full transition-colors duration-300 ${
          scrolled
            ? "bg-background-50/95 backdrop-blur border-b border-background-200/80"
            : "bg-background-50 border-b border-transparent"
        }`}
      >
        <div className="container-page flex items-center justify-between gap-4 h-16">
          <Link to="/" className="flex items-center gap-2.5 group cursor-pointer shrink-0">
            <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center text-lg group-hover:bg-primary-600 transition-colors">
              <i className="ri-emotion-laugh-line"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-lg text-foreground-950 font-heading">{site.brand}</span>
              <span className="text-[10px] tracking-widest text-foreground-400 mt-0.5 hidden sm:block">
                CHIGUA · 边吃瓜边玩梗
              </span>
            </span>
          </Link>

          <nav aria-label="主导航" className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
                    isActive
                      ? "text-primary-600 bg-primary-50"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div ref={searchRef} className="relative hidden sm:block">
              <div className="flex items-center h-9 w-44 lg:w-56 rounded-md border border-background-300 bg-background-100/70 px-3 focus-within:border-primary-400 transition-colors">
                <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-line"></i>
                </span>
                <input
                  value={keyword}
                  onChange={(event) => {
                    setKeyword(event.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && results[0]) goToResult(results[0].id);
                  }}
                  placeholder="搜梗、搜段子"
                  aria-label="站内搜索"
                  className="flex-1 bg-transparent border-0 outline-none text-sm text-foreground-900 placeholder:text-foreground-400 px-2"
                />
              </div>
              {searchOpen && keyword.trim() ? (
                <div className="absolute right-0 mt-2 w-72 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                  {results.length > 0 ? (
                    <ul>
                      {results.map((post) => (
                        <li key={post.id}>
                          <button
                            type="button"
                            onClick={() => goToResult(post.id)}
                            className="w-full text-left px-3 py-2.5 hover:bg-background-100 transition-colors cursor-pointer"
                          >
                            <span className="block text-sm text-foreground-900 line-clamp-1">{post.title}</span>
                            <span className="block text-xs text-foreground-400 mt-0.5">{post.author}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="px-3 py-4 text-sm text-foreground-500">没有找到相关瓜，换个词再试试</p>
                  )}
                </div>
              ) : null}
            </div>

            <Link
              to="/about#submit"
              className="hidden md:inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-send-plane-line"></i>
              </span>
              我要投稿
            </Link>

            <button
              type="button"
              aria-label={menuOpen ? "收起菜单" : "展开菜单"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
              className="lg:hidden w-9 h-9 rounded-md border border-background-300 text-foreground-800 flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className={menuOpen ? "ri-close-line" : "ri-menu-line"}></i>
            </button>
          </div>
        </div>

        {menuOpen ? (
          <div className="lg:hidden border-t border-background-200 bg-background-50">
            <div className="container-page py-4 flex flex-col gap-1">
              <div className="flex items-center h-10 rounded-md border border-background-300 bg-background-100/70 px-3 mb-2">
                <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-line"></i>
                </span>
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && results[0]) goToResult(results[0].id);
                  }}
                  placeholder="搜梗、搜段子"
                  aria-label="站内搜索"
                  className="flex-1 bg-transparent border-0 outline-none text-sm px-2"
                />
              </div>
              {keyword.trim() && results.length > 0 ? (
                <ul className="mb-2">
                  {results.map((post) => (
                    <li key={post.id}>
                      <button
                        type="button"
                        onClick={() => goToResult(post.id)}
                        className="w-full text-left px-3 py-2 rounded-md hover:bg-background-100 text-sm text-foreground-800 cursor-pointer"
                      >
                        {post.title}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/"}
                  className={({ isActive }) =>
                    `px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer ${
                      isActive ? "text-primary-600 bg-primary-50" : "text-foreground-700 hover:bg-background-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/about#submit"
                className="mt-2 inline-flex items-center justify-center gap-1.5 h-10 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-send-plane-line"></i>
                </span>
                我要投稿
              </Link>
            </div>
          </div>
        ) : null}
      </div>
    </header>
  );
}